import { Component, ChangeDetectionStrategy, OnInit, ElementRef, OnDestroy, ChangeDetectorRef, Input } from "@angular/core";
import { FormControl } from "@angular/forms";
import { State, Store, Required } from "@vattenfall/core";
import { Subscription, of } from "rxjs";
import { distinctUntilChanged, debounceTime, switchMap } from "rxjs/operators";

import { AutocompleteSearch, AutocompleteSearchHit } from "./autocomplete-search.model";
import { AutocompleteSearchService } from "./autocomplete-search.service";

/**
 * Page search element that displays a list of related search results.
 *
 * The editor must specify a the URL to the search page and optionally a placeholder text for the search field. The element can be navigated using keyboard events.
 */
@Component({
  selector: "autocomplete-search",
  templateUrl: "./autocomplete-search.element.html",
  styleUrls: ["./autocomplete-search.element.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteSearchElement extends State(AutocompleteSearch) implements OnInit, OnDestroy {
  /**
   * The URL to redirect to on form submit
   */
  @Input()
  @Required()
  public redirectUrl?: string;
  /**
   * Search field placeholder text
   */
  @Input()
  public placeholder = "";

  search = new FormControl();
  searchSubscription = Subscription.EMPTY;

  hits: Array<AutocompleteSearchHit> = [];

  private get options() {
    return this.elementRef.nativeElement.querySelectorAll<HTMLLIElement>("[role='option']");
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private store: Store<AutocompleteSearch>,
    private autocompleteSearchService: AutocompleteSearchService
  ) {
    super();
    super.setStore(store);
  }

  ngOnInit() {
    this.searchSubscription = this.search.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((search) =>
          search ? this.autocompleteSearchService.getAutocompleteSearch(search) : of({ state: "ok", hits: [] })
        )
      )
      .subscribe(this.store.setState("completed"));
  }

  ngOnDestroy() {
    this.searchSubscription.unsubscribe();
  }

  /**
   * Handle keydown events for keyboard users
   */
  onKeydown(event: KeyboardEvent) {
    // Set focus on the first option when the user press down arrow while
    // on the input element
    if (event.currentTarget instanceof HTMLInputElement) {
      if (event.key === "ArrowDown" && this.options.item(0)) this.options.item(0).focus();
      return;
    }
    const oldOption = event.currentTarget as HTMLLIElement;
    // Get the index of the current tab in the tabs node list
    const oldOptionIndex = Array.from(this.options).findIndex((option) => option.isSameNode(oldOption));
    // Work out which key the user is pressing
    switch (event.key) {
      // If the up or left arrow key is pressed, switch to the next option
      case "ArrowLeft":
      case "ArrowUp": {
        // Get the index of the new option, will be last in order if the old option is the first in order
        const newOptionIndex = oldOptionIndex === 0 ? this.options.length - 1 : oldOptionIndex - 1;
        this.switchOption(oldOption, this.options.item(newOptionIndex));
        break;
      }
      // If the up or right arrow key is pressed, switch to the previous option
      case "ArrowRight":
      case "ArrowDown": {
        // Get the index of the new option, will be first in order if the old option is the last in order
        const newOptionIndex = oldOptionIndex === this.options.length - 1 ? 0 : oldOptionIndex + 1;
        this.switchOption(oldOption, this.options.item(newOptionIndex));
        break;
      }
      // If enter is pressed, set value of input field and submit form
      case "Enter": {
        this.redirect(oldOption.id);
      }
    }
  }

  /**
   * Handle clicking of options for mouse users
   */
  onClick(event: MouseEvent) {
    this.redirect((event.currentTarget as HTMLLIElement).id);
  }

  /**
   * The option switching function
   */
  private switchOption(oldOption: HTMLLIElement, newOption: HTMLLIElement) {
    // Set the selected state
    newOption.setAttribute("aria-selected", "true");
    oldOption.removeAttribute("aria-selected");
    newOption.focus();
  }

  /**
   * Redirects to search page
   */
  private redirect(query: string) {
    // Close dropdown
    this.hits = [];
    this.changeDetectorRef.detectChanges();
    // Redirect using searc string
    window.location.href = `${this.redirectUrl}?query=${encodeURIComponent(query)}`;
  }
}
